import clsx from "clsx";
import React, { FC, useEffect } from "react";
import { useInView } from "react-intersection-observer";

import { useTheme, withThemeProvider } from "@narvar/nth-hook-theme";
import { useViewportConfig } from "@narvar/nth-kit-returns-headless";
import { zIndex } from "../../consts/theme";
import { useStyles } from "./StickyFooterStyles";
import { StickyFooterProps } from "./types";

const StickyFooter: FC<StickyFooterProps> = ({
  absolute,
  fixed,
  removePadding,
  onShow,
  children,
}) => {
  const { panelcontainerNoneNone, documentbodyNoneNone } = useTheme();
  const viewportConfig = useViewportConfig();

  const styles = useStyles();

  const { ref, inView } = useInView({
    threshold: 0,
  });
  const inline = inView && !fixed && !absolute;
  useEffect(() => {
    onShow?.({
      inView,
      display: inline ? "inline" : "footer",
    });
  }, [inView, inline, onShow]);

  const footerWrapperClasses = clsx({
    [styles.footerWrapper]: true,
    [styles.footerWrapperNoPadding]:
      removePadding === "always" ||
      (removePadding === "inline" && inline) ||
      (removePadding === "footer" && !inline),
  });

  const footerStyles = {
    zIndex: zIndex.AppBar,
    bottom: `${viewportConfig.anchor?.offsetBottom ?? 0}px`,
    left: `${viewportConfig.anchor?.offsetLeft ?? 0}px`,
    right: `${viewportConfig.anchor?.offsetRight ?? 0}px`,
    ...documentbodyNoneNone,
    backgroundColor: documentbodyNoneNone?.backgroundColor ?? "#fff",
    borderTopColor: panelcontainerNoneNone?.borderTopColor || "#ccc",
    borderTopWidth: panelcontainerNoneNone?.borderTopWidth || "1px",
    borderTopStyle: panelcontainerNoneNone?.borderTopStyle || "solid",
  };

  return (
    <div className={footerWrapperClasses} ref={ref}>
      {inline && <div>{children}</div>}
      {absolute && (
        <div className={styles.absoluteContainer} style={footerStyles}>
          {children}
        </div>
      )}
      {!inline && !absolute && (
        <div className={styles.stickyContainer} style={{ ...footerStyles }}>
          {children}
        </div>
      )}
    </div>
  );
};

export default withThemeProvider(StickyFooter, {
  themeTokens: ["documentbodyNoneNone", "panelcontainerNoneNone"],
});
